﻿import React, { useState, useEffect } from 'react';
import { UserDataStore } from '../store/userDataStore';
import { UvVisSpectroscopyBenchSheet } from '../models/uv-vis-spectroscopy-bench-sheet';
import { BenchSheetTechnologyType, DocumentType, NoteType, Entities } from '../models/enums';
import SecondaryBenchSheetInstrument from './SecondaryBenchSheetInstrument';
import SamplePreparationsComponent from './SamplePreparations/SamplePreparationsComponent';
import QCCheckComponent from './QCCheckComponent';
import MeasuredData from './MeasuredData';
import BenchSheetHeaderComponent from './BenchSheetHeaderComponent';
import NoteViewer from './NoteViewer';
import BenchSheetData from './BenchSheetData';
import { CrudFunctions } from '../utilities/crud-functions';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import { ItemBasicInformation } from '../models/item-basic-information';
import { DocumentationConstants } from '../store/documentation-constants';
import TicketLogEntryForm from './TicketLogEntryForm';
import BenchSheetToolbar from './BenchSheetToolbar';
import { FieldUpdate } from '../models/field-update';
import DmaFooter from './DmaFooter';

import { Popup as DialogPopup } from 'devextreme-react/popup';
import SelectBox from 'devextreme-react/select-box';
import TextBox from 'devextreme-react/text-box';

//temp
const methodTypes = [
    { id: 1, name: "Absorbance" },
    { id: 2, name: "% Transmittance" }
]

const cellLengths = [
    { id: 1, name: "1 mm" },
    { id: 2, name: "1 cm" }
]

let key = 1;

export default function UvVisSpectroscopyBenchSheetForm() {
    const [canEdit, setCanEdit] =
        useState<boolean>(false);

    const [benchSheet, setBenchSheet] =
        useState<UvVisSpectroscopyBenchSheet>();

    const [ticketLogEntryDialogVisible, setTicketLogEntryDialogVisible] =
        useState<boolean>(false);

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const [dataLoaded, setDataLoaded] =
        useState<boolean>();

    useEffect(() => {
        setAuthorizations();
        getAllData();
    }, []);

    const parts = window.location.href.split('/');
    const benchSheetId = parts[parts.length - 1];

    key++;

    return (
        <div className="pw-form-container">
            <div className="pw-content">
                <BenchSheetToolbar title="UV-VIS Bench Sheet"
                    subtitle="UV-Visible Spectroscopy"
                    auditRowPointer={benchSheetId} entity={Entities.UvVisBenchSheets}
                    technologyId={BenchSheetTechnologyType.UvVisSpectroscopy}
                    documentationId={DocumentationConstants.UVVISBenchSheets}
                    showItemInformation={showBenchSheetInformationDialog}
                    showTicketLog={showTicketLogEntryDialog} />

                <BenchSheetHeaderComponent benchSheetId={benchSheetId} readOnly={!canEdit} />

                <div className='pw-comp-row pw-margin-top-1'>
                    <div className="pw-form-unit pw-comp-20">
                        <div className="pw-form-unit-label">Method</div>
                        <SelectBox id="methodId"
                            className="selectbox"
                            placeholder='NA' readOnly={!canEdit}
                            displayExpr='name' valueExpr='id'
                            dataSource={methodTypes ? methodTypes : []}
                            onValueChanged={onValueChanged}
                            value={benchSheet && benchSheet.methodId ?
                                benchSheet.methodId : null} />
                    </div>

                    <div className="pw-form-unit pw-comp-20">
                        <div className="pw-form-unit-label">Cell Length</div>
                        <SelectBox id="cellLengthId"
                            className="selectbox"
                            placeholder='NA' readOnly={!canEdit}
                            displayExpr='name' valueExpr='id'
                            dataSource={cellLengths ? cellLengths : []}
                            onValueChanged={onValueChanged}
                            value={benchSheet && benchSheet.cellLengthId ?
                                benchSheet.cellLengthId : null} />
                    </div>
                </div>

                <div className='pw-comp-row'>
                    <div className="pw-form-unit pw-comp-30">
                        <div className="pw-form-unit-label">Wavelengths (nm)</div>
                        <TextBox id="wavelengths" readOnly={!canEdit}
                            placeholder='NA' className="textbox"
                            onValueChanged={onValueChanged}
                            value={benchSheet && benchSheet.wavelengths ? benchSheet.wavelengths : undefined} />
                    </div>
                </div>

                <SecondaryBenchSheetInstrument benchSheetId={benchSheetId} isReadOnly={!canEdit}
                    technology={BenchSheetTechnologyType.UvVisSpectroscopy}
                    title="UV-VIS Instrument" />

                <QCCheckComponent benchSheetId={benchSheetId} isReadOnly={!canEdit}
                    technologyId={BenchSheetTechnologyType.UvVisSpectroscopy}
                    datagridHeight='12rem' title='UV-VIS QC Checks' />

                <div className="pw-margin-bottom-05">
                    <SamplePreparationsComponent benchSheetId={benchSheetId} diluent={true} />
                </div>

                <SamplePreparationsComponent benchSheetId={benchSheetId} diluent={false} />

                <div className="pw-comp-row-scroll-fixed-grid">
                    <MeasuredData benchSheetId={benchSheetId} readOnly={!canEdit}
                        datagridHeight='30em' title='Analysis Data'
                        isReadOnly={false} />
                </div>

                <div className="pw-comp-row-scroll-fixed-grid">
                    <NoteViewer height='10rem' title="Bench Sheet Note"
                        noteType={NoteType.Method} parentId={benchSheetId} />
                </div>

                <div className="pw-comp-row-scroll-fixed-grid">
                    <BenchSheetData parentId={benchSheetId}
                        readOnly={!canEdit} canDelete={true}
                        technology={BenchSheetTechnologyType.UvVisSpectroscopy}
                        documentType={DocumentType.General}
                        hideImageHandler={true} />
                </div>

            </div >

            <DmaFooter />

            {
                itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {
                ticketLogEntryDialogVisible &&
                <DialogPopup visible={ticketLogEntryDialogVisible}
                    onHiding={hideTicketLogEntryDialog}
                    title="Ticket Log Entry"
                    width={'55rem'}
                    height={'45rem'}
                    dragEnabled={true}
                    resizeEnabled={true}>
                    <TicketLogEntryForm formName="Conductivity Bench Sheet" contextData={benchSheet}
                        onCancel={hideTicketLogEntryDialog}
                        onComplete={hideTicketLogEntryDialog}
                        key={`TLE${key}`} />
                </DialogPopup>
            }

            {
                globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }
        </div >
    );

    function setAuthorizations() {
        const user = UserDataStore.getCurrentUser();

        const canEditRoles = ["SA", "MF-A", "MF-L", "MF-T", "PP-A", "PP-L", "PP-T", "DIST-A", "BA-A", "QCP-A", "QCD-A", "RD-A", "RD-L", "RD-T"];
        const canEdit = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));

        setCanEdit(canEdit);
    }

    async function onValueChanged(e: any) {
        if (!dataLoaded)
            return;

        const caller = e.element.id as string;

        if (caller && benchSheet) {
            const bs = benchSheet as any;
            const currentValue = bs[caller];

            if (currentValue === e.value)
                return;
            else if (!currentValue && !e.value)
                return;
            else {
                setBenchSheet({ ...benchSheet, [caller]: e.value });

                let fieldUpdate: FieldUpdate = {
                    entity: "UvVisSpectroscopyBenchSheets",
                    field: caller,
                    value: e.value,
                    rowId: benchSheetId
                };

                await updateField(fieldUpdate)
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating Field");
                    });
            }
        }
    }

    //GET methods

    async function getBenchSheet() {
        await CrudFunctions.getData(`benchsheets/benchsheet/${benchSheetId}`)
            .then(data => {
                setBenchSheet(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Bench Sheet");
            });
    }

    // PUT functions

    async function updateField(fieldUpdate: FieldUpdate) {
        return CrudFunctions.putData("benchsheets/uvvisbenchsheetfield", fieldUpdate);
    }

    // other functions

    async function getAllData() {
        await getBenchSheet()
            .then(() => setDataLoaded(true));
    }

    function showTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(true);
    }

    function hideTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(false);
    }

    function showBenchSheetInformationDialog() {
        if (benchSheet) {
            let itemInformation: ItemBasicInformation = {
                rowPointer: benchSheet.benchSheetId,
                createTimestamp: benchSheet.createTimestamp,
                lastModifiedTimestamp: benchSheet.modifyTimestamp,
                createUser: benchSheet.createUser,
                modifyUser: benchSheet.modifyUser
            };

            setItemBasicInformation(itemInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}